// frontend/src/features/ups/UpsRequestsDriverList.js
// Phase 8.7D: UPS "Requests" tab
//
// Purpose:
// - Show assignment requests created by agencies (status = 'requested').
// - UPS can approve them -> status transitions requested -> confirmed.
// - Once approved, they disappear from Requests and appear in Reserved.
//
// Data source:
// - Uses the same UPS site assignments read model as Reserved/Active.
// - Just passes statuses: ['requested'].
//
// Phase 8.7D cleanup:
// - Do NOT pass agencyId/search into loadSiteAssignments payload (thunk reads from Redux).
// - Keep effect deps on shared filter fields so refetch occurs when filters change.

// Phase 09 note:
// - This tab is intentionally NOT time-bucket-specific.
// - Requests is an approval queue, not a time-sliced operational lane.
// - Primary truth here is assignment_status = 'requested'.
// - Therefore we pass an explicit broad query:
//     statuses: ['requested']
//     timeBucket: undefined
// - This makes the intent clear during Phase 09 cleanup:
//   time filtering is intentionally not applied here yet.
// - If later needed, date/time filtering can be added as a refinement
//   without changing the core doctrine of this page.

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye } from 'lucide-react'; // ✅ Added

import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';
import { selectConcreteSelectedSiteId, selectIsAllSitesMode, } from '../sites/sitesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import { upsertDriver } from '../drivers/driverSlice'; // ✅ Added

import { approveAssignment, cancelAssignment } from '../../services/assignmentService';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverDetailsModal from './modals/DriverDetailsModal'; // ✅ Added

import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES } from './ui';

const REQUEST_ASSIGNMENT_QUERY = {
  statuses: ['requested'],
  timeBucket: undefined,
};

export default function UpsRequestsDriverList() {
  const dispatch = useDispatch();

  const siteId = useSelector(selectConcreteSelectedSiteId);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);
  const filters = useSelector(selectFilters);

  const selectRequested = useMemo(() => makeSelectAssignmentsForTab('requested'), []);
  const requested = useSelector(selectRequested);

  const [acting, setActing] = useState(() => new Set());

  // ✅ 1. Details Modal State
  const [showDetails, setShowDetails] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);

useEffect(() => {
  if (!isAllSitesMode && !siteId) return;
  dispatch(loadSiteAssignments({ ...REQUEST_ASSIGNMENT_QUERY }));
}, [dispatch, siteId, isAllSitesMode, filters?.agencyId, filters?.q, filters?.source]);

  const rows = useMemo(() => {
    return (requested || []).filter((r) => !r?.is_banned);
  }, [requested]);

  // ✅ 2. Modal Helpers
  const openDetails = (row) => {
    // Sync to drivers slice so the modal can read "live" data (photos, etc)
    dispatch(upsertDriver(row));
    setDetailsRow(row);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsRow(null);
  };

  async function refresh() {
    // let the thunk choose site-scoped vs all-sites read mode.
    dispatch(loadSiteAssignments({ ...REQUEST_ASSIGNMENT_QUERY }));
    dispatch(bumpStatsRefreshKey());
  }

  const onApprove = async (row) => {
    const assignmentId = Number(row?.assignment_id || row?.id);
    const actionSiteId = row?.site_id || siteId; // Phase 5: use row site in All Sites mode.

    if (!assignmentId || !actionSiteId || acting.has(assignmentId)) return;

    setActing((prev) => new Set(prev).add(assignmentId));

    try {
      await approveAssignment(actionSiteId, assignmentId);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || 'Approve failed');
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  const onReject = async (row) => {
    const assignmentId = Number(row?.assignment_id || row?.id);
    const actionSiteId = row?.site_id || siteId; // Phase 5: use row site in All Sites mode.

    if (!assignmentId || !actionSiteId || acting.has(assignmentId)) return;

    setActing((prev) => new Set(prev).add(assignmentId));

    try {
      await cancelAssignment(actionSiteId, assignmentId);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || 'Reject failed');
      await refresh();
    } finally {
      setActing((prev) => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  return (
    <>
      <section className="my-6 overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-app-text">Request Queue</h3>
            <p className="mt-1 text-sm text-app-muted">
              Review agency requests and approve drivers into the reserved lane.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {rows.length} pending
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['Driver', 'Vehicle', 'Owner', 'Site', 'Submitted', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {rows.length === 0 ? (
                <tr>
                  <td className="px-6 py-12 text-center text-sm text-app-muted" colSpan={6}>
                    No pending requests.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const assignmentKey = Number(r.assignment_id || r.id);
                  const isActing = acting.has(assignmentKey);

                  return (
                    <tr
                      key={r.assignment_id || r.id}
                      className="transition hover:bg-app-surface"
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-app-accent bg-app-primary text-xs font-bold uppercase text-app-accent">
                            {(r.name || '—')
                              .split(' ')
                              .map((part) => part[0])
                              .join('')
                              .slice(0, 2)}
                          </div>

                          <div>
                            <div className="font-semibold text-app-text">{r?.name || '—'}</div>
                            <div className="text-xs text-app-muted">
                              Request ID · {r.assignment_id ? `ASN-${r.assignment_id}` : '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                        {r?.vehicle_registration_number || '—'}
                      </td>

                      <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                        {formatOwnerLabel(r)}
                      </td>

                      <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                        {r?.site_name || '—'}
                      </td>

                      <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                        {r?.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </td>

                      <td className="px-6 py-5 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openDetails(r)}
                            className={COMPANY_ACTION_CLASSES.compactSecondary}
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={() => onApprove(r)}
                            disabled={isActing}
                            className={
                              isActing
                                ? 'rounded-xl border px-4 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                                : COMPANY_ACTION_CLASSES.compactSuccess
                            }
                          >
                            Approve
                          </button>

                          <button
                            onClick={() => onReject(r)}
                            disabled={isActing}
                            className={
                              isActing
                                ? 'rounded-xl border px-4 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                                : COMPANY_ACTION_CLASSES.compactSecondary
                            }
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {rows.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              No pending requests.
            </div>
          ) : (
            rows.map((r) => {
              const assignmentKey = Number(r.assignment_id || r.id);
              const isActing = acting.has(assignmentKey);
              const requestDate = r?.created_at || r?.submission_date;
              const avatarText = (r.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <DriverMobileCard
                  key={r.assignment_id || r.id}
                  tone="request"
                  title={r?.name || '—'}
                  subtitle={`Request ID · ${r.assignment_id ? `ASN-${r.assignment_id}` : '—'}`}
                  avatarText={avatarText}
                  fields={[
                    ['Vehicle', r?.vehicle_registration_number],
                    ['Owner', formatOwnerLabel(r)],
                    ['Site', r?.site_name],
                    ['Requested', requestDate ? new Date(requestDate).toLocaleString() : null],
                    ['Phone', r?.phone],
                    ['Email', r?.email],
                  ]}
                  actions={
                    <>
                      <button
                        onClick={() => openDetails(r)}
                        className={COMPANY_ACTION_CLASSES.compactSecondary}
                        title="View details"
                      >
                        <Eye size={16} />
                      </button>

                      <button
                        onClick={() => onApprove(r)}
                        disabled={isActing}
                        className={
                          isActing
                            ? 'rounded-xl border px-4 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                            : COMPANY_ACTION_CLASSES.compactSuccess
                        }
                      >
                        Approve
                      </button>

                      <button
                        onClick={() => onReject(r)}
                        disabled={isActing}
                        className={
                          isActing
                            ? 'rounded-xl border px-4 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                            : COMPANY_ACTION_CLASSES.compactSecondary
                        }
                      >
                        Reject
                      </button>
                    </>
                  }
                />
              );
            })
          )}
        </div>
      </section>

      {showDetails && detailsRow && (
        <DriverDetailsModal driver={detailsRow} onClose={closeDetails} />
      )}
    </>
  );}
